// Twilio Helper Functions

let trackOwnerCache = {}

function getAvgProp (reports, prop) {

  const mappedProps = reports.map(function (report) {
    return Number.isInteger(report[prop]) ? report[prop] : null
  })

  const validMappedProps = mappedProps.filter(function (prop) { return (prop !== null || prop !== undefined) })
  const sum = validMappedProps.reduce(function (a, b) { return a + b }, 0)
  const avg = sum / validMappedProps.length
  return isNaN(avg) ? 0 : avg
}

function getMaxProp (reports, prop) {
  const mappedProps = reports.map(function (report) {
    return Number.isInteger(report[prop]) ? report[prop] : 0
  })
  return mappedProps.reduce(function (a, b) {
    return Math.max(a, b)
  }, 0)
}

export function getAvgJitter (reports) {
  return getAvgProp(reports, 'jitter')
}

export function getAvgAudioLevel (reports) {
  return getAvgProp(reports, 'audioLevel')
}

export function getAvgLatency (reports) {
  return getAvgProp(reports, 'roundTripTime')
}

export function getAvgFrameRate (reports) {
  return getAvgProp(reports, 'frameRate')
}

export function getMaxAudioLevel (reports) {
  return getMaxProp(reports, 'audioLevel')
}

export function getPercentagePacketLoss (reports) {

  const first = reports[0] || {packetsLost: 0, packetsSent: 0}
  const last = reports[reports.length - 1] || {packetsLost: 0, packetsSent: 1}
  const packetsSent = last.packetsSent - first.packetsSent
  const packetsLost = last.packetsLost - first.packetsLost
  const packetsReceived = last.packetsReceived - first.packetsReceived
  const packetsSentLossPercentage = (packetsLost / packetsSent) * 100
  const packetsReceivedLossPercentage = (packetsLost / packetsReceived) * 100

  if (packetsSent) {
    return packetsSentLossPercentage
  }
  if (packetsReceived) {
    return packetsReceivedLossPercentage
  }
  return 0
}

function getCallQualityText (latency, packetLoss) {
  if (packetLoss > 7 || latency > 1000) {
    return 'danger'
  }
  if (packetLoss > 2 || latency > 750) {
    return 'warning'
  }
  if (packetLoss > 1 || latency > 500) {
    return 'info'
  }
  return 'success'
}

function getCallQualityNumber (latency, packetLoss) {
  if (packetLoss > 7 || latency > 1000) {
    return 1
  }
  if (packetLoss > 2 || latency > 750) {
    return 2
  }
  if (packetLoss > 1 || latency > 500) {
    return 3
  }
  return 4
}

export function analyseReports (reports, slidingWindow = null) {
  const avgLatency = getAvgLatency(reports)
  const percentagePacketLoss = getPercentagePacketLoss(reports)
  const callQualityNumber = getCallQualityNumber(avgLatency, percentagePacketLoss)
  const callQualityText = getCallQualityText(avgLatency, percentagePacketLoss)

  return {
    'avgLatency': avgLatency,
    'percentagePacketLoss': percentagePacketLoss,
    'callQualityNumber': callQualityNumber,
    'callQualityText': callQualityText,
    'sampleSize': reports.length
  }
}


export function getParticipantByTrackId (room, trackId, trackSid) {
  if (trackOwnerCache[trackId]) {
    return trackOwnerCache[trackId]
  } else {
    const trackOwner = [...room.participants.values(), room.localParticipant].find((part) => part.tracks.get(trackSid))
    trackOwnerCache[trackId] = trackOwner
    return trackOwner
  }
}

export function getParticipantByLearncubeId (room, learncubeId) {
  return [...room.participants.values(), room.localParticipant].find((part) => part.identity === learncubeId)
}

export function defaultMediaOpts () {
  return {
    video: {
      label: '',
      name: '',
      deviceId: undefined,
      // facingMode: 'user',
      aspectRatio: 1.346153846153846,
      frameRate: 10,
      width: 280,
      maxVideoBitrate: 500000 // twilio minimum
    },
    audio: {
      label: '',
      name: '',
      deviceId: undefined
    }
  }
}

export function defaultTracks () {
  return []
}

export function sendDataMsg (sender, receiver, action, payload) {
  const dataTrackPublication = sender.dataTracks.size && sender.dataTracks.values().next().value
  if (dataTrackPublication && dataTrackPublication.track) {
    dataTrackPublication.track.send(JSON.stringify({'action': action, 'userId': receiver, 'payload': payload}))
  }
  // else {
  //   throw new Error(`Senders Data Track is not ready... ${sender.identity}`)
  // }
}

export function twilioMediaInput (options, media) {
  if (!options[media]) {
    return 'disabled'
  } else if (options[media].deviceId === undefined) {
    return ''
  } else {
    return options[media].deviceId.exact
  }
}

export function twilioMediaOutput (options, media) {
  if (!options[media]) {
    return ''
  } else if (options[media].deviceId === undefined) {
    return ''
  } else {
    return options[media].deviceId.exact
  }
}

export function labelRemoteTracks (report, room, userId) {

  const remoteTracks = [...report.remoteAudioTrackStats, ...report.remoteVideoTrackStats]

  remoteTracks.forEach((track) => {
    const trackOwner = getParticipantByTrackId(room, track.trackId, track.trackSid) || {}
    track.lcLabel = `${trackOwner.id}_${userId}`
    track.senderId = trackOwner.id
    track.receiverId = userId
  })
  return {...report, 'provider': 'twilio'}
}

export function getPeerConnectionStatsById (room, pcId) {
  const peerConnection = room._signaling._peerConnectionManager._peerConnections.get(pcId)._peerConnection
  return peerConnection.getStats().then((stats) => {
    return [...stats.values()].find(stat => stat.type === 'candidate-pair')
    // return [...stats.values()].find(stat => stat.framesDropped !== undefined)
  })
}

export function formatRoom (room, roomType) {

  const participantProps = [
    'sid', 'id', 'username', 'first_name', 'last_name', 'state', 'identity', 'browser', 'countryCode'
  ]

  const trackProps = [
    'kind', 'trackName', 'trackSid', 'isTrackEnabled'
  ]

  function objectifyParticipant (part, o = {}) {
    participantProps.forEach((prop) => {
      o[prop] = part[prop]
    })
    o.tracks = [...part.videoTracks.values(), ...part.audioTracks.values()].map(track => objectifyTrack(track))
    return o
  }

  function objectifyTrack (track, o = {}) {
    trackProps.forEach((prop) => {
      o[prop] = track[prop]
    })
    if (track.track) {
      o.mediaStreamTrack = !!track.track.mediaStreamTrack
      o.label = o.mediaStreamTrack ? track.track.mediaStreamTrack.label : null
      o.muted = o.mediaStreamTrack ? track.track.mediaStreamTrack.muted : null
    }
    return o
  }

  return {
    'name': room.name,
    'sid': room.sid,
    'type': roomType,
    'localParticipant': objectifyParticipant(room.localParticipant),
    'remoteParticipants': [...room.participants.values()].map(part => objectifyParticipant(part))
  }
}
